import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';

import { adminAPI } from '../../services/api';
import { usePermissions } from '../../context/PermissionsContext';
import { useAdminToast } from '../../hooks/useAdminToast';

import {
  Plus, Edit2, Trash2, X, Eye, DollarSign,
  Users, TrendingUp, Clock, CheckCircle, XCircle,
  ToggleLeft, ToggleRight, Rocket, Image, AlertTriangle
} from 'lucide-react';

const AdminIDO = () => {
  const { hasPermission, isSuperAdmin } = usePermissions();
  const { toast } = useAdminToast();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);

  // Permission checks
  const canCreate = isSuperAdmin || hasPermission('ido', 'create');
  const canEdit = isSuperAdmin || hasPermission('ido', 'edit');
  const canView = isSuperAdmin || hasPermission('ido', 'view');
  const canDelete = isSuperAdmin || hasPermission('ido', 'delete');

  useEffect(() => {
    if (canView) {
      loadProjects();
    }
  }, [canView]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getIDOProjects();
      setProjects(res.data.projects || []);
    } catch (err) {
      console.error('Failed to load IDO projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleBuyEnabled = async (project) => {
    try {
      await adminAPI.updateIDOProject(project.id, { buy_enabled: !project.buy_enabled });
      loadProjects();
      toast.success(project.buy_enabled ? 'Покупка отключена' : 'Покупка включена');
    } catch (err) {
      toast.apiError(err);
    }
  };

  const toggleSellEnabled = async (project) => {
    try {
      await adminAPI.updateIDOProject(project.id, { sell_enabled: !project.sell_enabled });
      loadProjects();
      toast.success(project.sell_enabled ? 'Продажа отключена' : 'Продажа включена');
    } catch (err) {
      toast.apiError(err);
    }
  };

  const toggleLaunched = async (project) => {
    try {
      await adminAPI.updateIDOProject(project.id, { is_launched: !project.is_launched });
      loadProjects();
      toast.success(project.is_launched ? 'Проект снят с запуска' : 'Проект запущен!');
    } catch (err) {
      toast.apiError(err);
    }
  };

  const openProjectDetail = async (project) => {
    try {
      const res = await adminAPI.getIDOProject(project.id);
      setSelectedProject(res.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Failed to load project details');
    }
  };

  const openDeleteModal = async (project) => {
    try {
      const res = await adminAPI.getIDODeletePreview(project.id);
      setDeletePreview(res.data);
      setShowDeleteModal(true);
    } catch (err) {
      toast.apiError(err, 'Не удалось загрузить информацию');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700' }}>
          Управление IDO
        </h1>
        {canCreate && (
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: COLORS.primary,
              border: 'none',
              borderRadius: '8px',
              color: '#000',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Plus size={18} />
            Создать IDO
          </button>
        )}
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: COLORS.textSecondary }}>
          Загрузка...
        </div>
      ) : projects.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px',
          backgroundColor: COLORS.card,
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
        }}>
          <Rocket size={48} color={COLORS.textSecondary} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <div style={{ color: COLORS.textSecondary, fontSize: '16px' }}>Нет IDO проектов</div>
          <div style={{ color: COLORS.textSecondary, fontSize: '14px', marginTop: '8px' }}>
            Создайте первый IDO проект
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {projects.map((project) => (
            <div
              key={project.id}
              style={{
                backgroundColor: COLORS.card,
                borderRadius: '12px',
                border: `1px solid ${COLORS.border}`,
                padding: '20px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {/* Logo */}
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    backgroundColor: COLORS.background,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}>
                    {project.logo ? (
                      <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ color: COLORS.primary, fontWeight: '700', fontSize: '18px' }}>
                        {project.symbol?.charAt(0)}
                      </span>
                    )}
                  </div>
                  
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>
                        {project.name}
                      </h3>
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: `${COLORS.primary}20`,
                        color: COLORS.primary,
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                      }}>
                        ${project.symbol}
                      </span>
                      {!project.is_launched && (
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: `${COLORS.warning}20`,
                          color: COLORS.warning,
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                        }}>
                          ⏳ Ожидает запуска
                        </span>
                      )}
                      {project.is_launched && (
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: `${COLORS.success}20`,
                          color: COLORS.success,
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                        }}>
                          ✓ Запущен
                        </span>
                      )}
                      {!project.is_active && (
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: `${COLORS.danger}20`,
                          color: COLORS.danger,
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                        }}>
                          Неактивен
                        </span>
                      )}
                    </div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '13px', marginTop: '4px' }}>
                      Запуск: {formatDate(project.launch_date)}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => openProjectDetail(project)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: COLORS.background,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      color: COLORS.textSecondary,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '13px',
                    }}
                  >
                    <Eye size={14} />
                    Детали
                  </button>
                  {canDelete && (
                    <button
                      onClick={() => openDeleteModal(project)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: `${COLORS.danger}15`,
                        border: `1px solid ${COLORS.danger}40`,
                        borderRadius: '6px',
                        color: COLORS.danger,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '13px',
                      }}
                    >
                      <Trash2 size={14} />
                      Удалить
                    </button>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                <StatBox label="Цена покупки" value={`$${project.buy_price}`} color={COLORS.success} />
                <StatBox label="Цена продажи" value={project.sell_price ? `$${project.sell_price}` : '-'} color={COLORS.danger} />
                <StatBox label="Собрано" value={`$${(project.total_raised || 0).toFixed(2)}`} color={COLORS.primary} />
                <StatBox label="Участники" value={project.participants || 0} color="#3b82f6" />
                <StatBox label="Продано" value={`${((project.sold_amount || 0)).toLocaleString()} / ${project.total_supply.toLocaleString()}`} color="#8b5cf6" />
              </div>

              {/* Progress */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Прогресс</span>
                  <span style={{ color: COLORS.text, fontSize: '12px', fontWeight: '600' }}>
                    {((project.sold_amount || 0) / project.total_supply * 100).toFixed(1)}%
                  </span>
                </div>
                <div style={{
                  height: '8px',
                  backgroundColor: COLORS.background,
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min((project.sold_amount || 0) / project.total_supply * 100, 100)}%`,
                    backgroundColor: COLORS.primary,
                    borderRadius: '4px',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              </div>

              {/* Controls - only show if user can edit */}
              {canEdit && (
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <ToggleControl
                    label="Запущен"
                    enabled={project.is_launched}
                    onToggle={() => toggleLaunched(project)}
                    color={project.is_launched ? COLORS.success : COLORS.textSecondary}
                  />
                  <ToggleControl
                    label="Покупка"
                    enabled={project.buy_enabled}
                    onToggle={() => toggleBuyEnabled(project)}
                  />
                  <ToggleControl
                    label="Продажа"
                    enabled={project.sell_enabled}
                    onToggle={() => toggleSellEnabled(project)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateIDOModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadProjects();
          }}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedProject && (
        <IDODetailModal
          project={selectedProject}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedProject(null);
          }}
          onUpdate={loadProjects}
          canEdit={canEdit}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && deletePreview && (
        <DeleteIDOModal
          preview={deletePreview}
          onClose={() => {
            setShowDeleteModal(false);
            setDeletePreview(null);
          }}
          onSuccess={() => {
            setShowDeleteModal(false);
            setDeletePreview(null);
            loadProjects();
          }}
        />
      )}
    </div>
  );
};

// ================== HELPER COMPONENTS ==================

const StatBox = ({ label, value, color }) => (
  <div style={{
    padding: '12px',
    backgroundColor: COLORS.background,
    borderRadius: '8px',
  }}>
    <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>{label}</div>
    <div style={{ color: color || COLORS.text, fontSize: '15px', fontWeight: '700' }}>{value}</div>
  </div>
);

const ToggleControl = ({ label, enabled, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 14px',
      backgroundColor: enabled ? `${COLORS.success}15` : COLORS.background,
      border: `1px solid ${enabled ? COLORS.success : COLORS.border}`,
      borderRadius: '8px',
      cursor: 'pointer',
    }}
  >
    {enabled ? (
      <ToggleRight size={18} color={COLORS.success} />
    ) : (
      <ToggleLeft size={18} color={COLORS.textSecondary} />
    )}
    <span style={{ color: enabled ? COLORS.success : COLORS.textSecondary, fontSize: '13px', fontWeight: '500' }}>
      {label}: {enabled ? 'Вкл' : 'Выкл'}
    </span>
  </div>
);

// ================== CREATE IDO MODAL ==================

const CreateIDOModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: '',
    symbol: '',
    launch_date: '',
    launch_time: '12:00',
    buy_price: '',
    sell_price: '',
    total_supply: '',
    min_purchase: '',
    max_purchase: '',
    description: '',
  });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      setError('Файл слишком большой. Максимум 2MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setLogo(reader.result);
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.symbol || !form.launch_date || !form.buy_price || !form.total_supply) {
      setError('Заполните обязательные поля: название, символ, дата запуска, цена покупки, количество токенов');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const launchDateTime = `${form.launch_date}T${form.launch_time}:00`;
      
      const data = {
        name: form.name,
        symbol: form.symbol.toUpperCase(),
        launch_date: launchDateTime,
        buy_price: parseFloat(form.buy_price),
        sell_price: form.sell_price ? parseFloat(form.sell_price) : null,
        total_supply: parseFloat(form.total_supply),
        min_purchase: form.min_purchase ? parseFloat(form.min_purchase) : null,
        max_purchase: form.max_purchase ? parseFloat(form.max_purchase) : null,
        description: form.description || null,
        logo_base64: logo || null,
      };

      await adminAPI.createIDOProject(data);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка создания IDO');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px',
    }} onClick={onClose}>
      <div
        style={{
          backgroundColor: COLORS.card,
          borderRadius: '16px',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>
            Создать новое IDO
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '20px' }}>
          {error && (
            <div style={{
              padding: '12px',
              backgroundColor: `${COLORS.danger}20`,
              borderRadius: '8px',
              color: COLORS.danger,
              fontSize: '13px',
              marginBottom: '16px',
            }}>
              {error}
            </div>
          )}

          {/* Logo Upload */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '8px' }}>
              Логотип токена
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '12px',
                backgroundColor: COLORS.background,
                border: `2px dashed ${COLORS.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}>
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Image size={24} color={COLORS.textSecondary} />
                )}
              </div>
              <div>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={handleLogoChange}
                  style={{ display: 'none' }}
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  style={{
                    padding: '10px 16px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'inline-block',
                  }}
                >
                  Загрузить
                </label>
                <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginTop: '6px' }}>
                  PNG, JPG, SVG до 2MB. Рекомендуемый размер 512×512
                </div>
              </div>
            </div>
          </div>

          {/* Name & Symbol */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <FormInput
              label="Название токена *"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              placeholder="Bitcoin Gold"
            />
            <FormInput
              label="Символ *"
              value={form.symbol}
              onChange={(v) => setForm({ ...form, symbol: v.toUpperCase() })}
              placeholder="BTG"
            />
          </div>

          {/* Launch Date & Time */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <FormInput
              label="Дата запуска *"
              value={form.launch_date}
              onChange={(v) => setForm({ ...form, launch_date: v })}
              type="date"
            />
            <FormInput
              label="Время (UTC) *"
              value={form.launch_time}
              onChange={(v) => setForm({ ...form, launch_time: v })}
              type="time"
            />
          </div>

          {/* Prices */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <FormInput
              label="Цена покупки (USDT) *"
              value={form.buy_price}
              onChange={(v) => setForm({ ...form, buy_price: v })}
              type="number"
              placeholder="0.05"
            />
            <FormInput
              label="Цена продажи (USDT)"
              value={form.sell_price}
              onChange={(v) => setForm({ ...form, sell_price: v })}
              type="number"
              placeholder="Можно установить позже"
            />
          </div>

          {/* Supply & Limits */}
          <FormInput
            label="Общее количество токенов (Hard Cap) *"
            value={form.total_supply}
            onChange={(v) => setForm({ ...form, total_supply: v })}
            type="number"
            placeholder="1000000"
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px', marginTop: '16px' }}>
            <FormInput
              label="Мин. покупка (USDT)"
              value={form.min_purchase}
              onChange={(v) => setForm({ ...form, min_purchase: v })}
              type="number"
              placeholder="10"
            />
            <FormInput
              label="Макс. покупка (USDT)"
              value={form.max_purchase}
              onChange={(v) => setForm({ ...form, max_purchase: v })}
              type="number"
              placeholder="10000"
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>
              Описание
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Описание проекта..."
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: COLORS.text,
                fontSize: '14px',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: loading ? COLORS.border : COLORS.primary,
              border: 'none',
              borderRadius: '8px',
              color: '#000',
              fontSize: '15px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Создание...' : 'Создать IDO'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================== IDO DETAIL MODAL ==================

const IDODetailModal = ({ project, onClose, onUpdate, canEdit = false }) => {
  const { toast } = useAdminToast();
  const [editMode, setEditMode] = useState(false);
  const [editProgressMode, setEditProgressMode] = useState(false);
  const [form, setForm] = useState({
    sell_price: project.project?.sell_price || '',
    sold_amount: project.project?.sold_amount || 0,
  });
  const [loading, setLoading] = useState(false);

  const updateSellPrice = async () => {
    if (!form.sell_price) return;
    setLoading(true);
    try {
      await adminAPI.updateIDOProject(project.project.id, {
        sell_price: parseFloat(form.sell_price)
      });
      onUpdate();
      setEditMode(false);
    } catch (err) {
      toast.apiError(err);
    } finally {
      setLoading(false);
    }
  };

  const updateProgress = async () => {
    const newAmount = parseFloat(form.sold_amount);
    if (isNaN(newAmount) || newAmount < 0) {
      toast.validation('Введите корректное значение');
      return;
    }
    if (newAmount > project.project.total_supply) {
      toast.validation('Количество не может превышать общее предложение');
      return;
    }
    setLoading(true);
    try {
      await adminAPI.updateIDOProject(project.project.id, {
        sold_amount: newAmount
      });
      onUpdate();
      setEditProgressMode(false);
    } catch (err) {
      toast.apiError(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ru-RU');
  };

  const p = project.project;
  const stats = project.stats;
  const transactions = project.transactions || [];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      justifyContent: 'flex-end',
      zIndex: 1000,
    }} onClick={onClose}>
      <div
        style={{
          width: '100%',
          maxWidth: '700px',
          height: '100%',
          backgroundColor: COLORS.card,
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              backgroundColor: COLORS.background,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}>
              {p?.logo ? (
                <img src={p.logo} alt={p.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ color: COLORS.primary, fontWeight: '700', fontSize: '18px' }}>{p?.symbol?.charAt(0)}</span>
              )}
            </div>
            <div>
              <h2 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>
                {p?.name}
              </h2>
              <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>${p?.symbol}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' }}>
            <StatBox label="Собрано USDT" value={`$${(stats?.total_raised || 0).toFixed(2)}`} color={COLORS.success} />
            <StatBox label="Участники" value={stats?.participants || 0} color="#3b82f6" />
            <StatBox label="Выкуплено" value={`$${(stats?.total_sold_back || 0).toFixed(2)}`} color={COLORS.danger} />
          </div>

          {/* Prices */}
          <div style={{
            backgroundColor: COLORS.background,
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
          }}>
            <h4 style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Цены</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Цена покупки</div>
                <div style={{ color: COLORS.success, fontSize: '20px', fontWeight: '700' }}>${p?.buy_price}</div>
              </div>
              <div>
                <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Цена продажи</div>
                {editMode ? (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                    <input
                      type="number"
                      value={form.sell_price}
                      onChange={(e) => setForm({ ...form, sell_price: e.target.value })}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: COLORS.card,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px',
                        width: '100px',
                      }}
                    />
                    <button
                      onClick={updateSellPrice}
                      disabled={loading}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: COLORS.primary,
                        border: 'none',
                        borderRadius: '6px',
                        color: '#000',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => setEditMode(false)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: COLORS.card,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.textSecondary,
                        cursor: 'pointer',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: COLORS.danger, fontSize: '20px', fontWeight: '700' }}>
                      {p?.sell_price ? `$${p.sell_price}` : 'Не установлена'}
                    </span>
                    {canEdit && (
                      <button
                        onClick={() => setEditMode(true)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: COLORS.card,
                          border: `1px solid ${COLORS.border}`,
                          borderRadius: '4px',
                          color: COLORS.textSecondary,
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        <Edit2 size={12} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Progress Section */}
          <div style={{
            backgroundColor: COLORS.background,
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600', margin: 0 }}>
                Прогресс продажи
              </h4>
              {!editProgressMode && canEdit && (
                <button
                  onClick={() => {
                    setForm({ ...form, sold_amount: p?.sold_amount || 0 });
                    setEditProgressMode(true);
                  }}
                  style={{
                    padding: '4px 10px',
                    backgroundColor: COLORS.card,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '4px',
                    color: COLORS.textSecondary,
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Edit2 size={12} />
                  Редактировать
                </button>
              )}
            </div>
            
            {editProgressMode ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>
                      Продано токенов
                    </label>
                    <input
                      type="number"
                      value={form.sold_amount}
                      onChange={(e) => setForm({ ...form, sold_amount: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: COLORS.card,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '14px', paddingTop: '20px' }}>
                    из {p?.total_supply?.toLocaleString()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={updateProgress}
                    disabled={loading}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: COLORS.primary,
                      border: 'none',
                      borderRadius: '6px',
                      color: '#000',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loading ? 'Сохранение...' : 'Сохранить'}
                  </button>
                  <button
                    onClick={() => setEditProgressMode(false)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: COLORS.card,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      color: COLORS.textSecondary,
                      cursor: 'pointer',
                    }}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
                    {(p?.sold_amount || 0).toLocaleString()} / {p?.total_supply?.toLocaleString()} токенов
                  </span>
                  <span style={{ color: COLORS.primary, fontSize: '13px', fontWeight: '600' }}>
                    {((p?.sold_amount || 0) / (p?.total_supply || 1) * 100).toFixed(1)}%
                  </span>
                </div>
                <div style={{
                  height: '10px',
                  backgroundColor: COLORS.card,
                  borderRadius: '5px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min((p?.sold_amount || 0) / (p?.total_supply || 1) * 100, 100)}%`,
                    backgroundColor: COLORS.primary,
                    borderRadius: '5px',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              </div>
            )}
          </div>

          {/* Recent Transactions */}
          <div>
            <h4 style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
              Последние операции
            </h4>
            {transactions.length === 0 ? (
              <div style={{ color: COLORS.textSecondary, fontSize: '14px', textAlign: 'center', padding: '24px' }}>
                Нет операций
              </div>
            ) : (
              <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                {transactions.map((tx, idx) => (
                  <div
                    key={tx.id || idx}
                    style={{
                      padding: '12px',
                      backgroundColor: COLORS.background,
                      borderRadius: '8px',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          backgroundColor: tx.type === 'buy' ? `${COLORS.success}20` : `${COLORS.danger}20`,
                          color: tx.type === 'buy' ? COLORS.success : COLORS.danger,
                          fontSize: '11px',
                          fontWeight: '600',
                        }}>
                          {tx.type === 'buy' ? 'ПОКУПКА' : 'ПРОДАЖА'}
                        </span>
                        <span style={{ color: COLORS.text, fontSize: '13px' }}>
                          {tx.user_email}
                        </span>
                      </div>
                      <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginTop: '4px' }}>
                        {formatDate(tx.created_at)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
                        {tx.token_amount.toLocaleString()} {p?.symbol}
                      </div>
                      <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                        ${tx.usdt_amount.toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ================== FORM INPUT ==================

const FormInput = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div>
    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '12px',
        backgroundColor: COLORS.background,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '8px',
        color: COLORS.text,
        fontSize: '14px',
        boxSizing: 'border-box',
      }}
    />
  </div>
);

// ================== DELETE IDO MODAL ==================

const DeleteIDOModal = ({ preview, onClose, onSuccess }) => {
  const { toast } = useAdminToast();
  const [refundOption, setRefundOption] = useState('none');
  const [customPrice, setCustomPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const project = preview.project;
  const affectedUsers = preview.affected_users_count || 0;
  const totalTokens = preview.total_tokens_to_refund || 0;

  const getRefundAmount = () => {
    if (refundOption === 'none') return 0;
    if (refundOption === 'buy_price') return preview.refund_options.buy_price.total_refund;
    if (refundOption === 'sell_price') return preview.refund_options.sell_price.total_refund;
    if (refundOption === 'custom') {
      const price = parseFloat(customPrice) || 0;
      return totalTokens * price;
    }
    return 0;
  };

  const handleDelete = async () => {
    if (confirmText !== project.symbol) {
      toast.validation(`Введите "${project.symbol}" для подтверждения`);
      return;
    }
    
    if (refundOption === 'custom' && (!customPrice || parseFloat(customPrice) <= 0)) {
      toast.validation('Введите корректную цену возврата');
      return;
    }

    setLoading(true);
    try {
      await adminAPI.deleteIDOProject(project.id, {
        refund_option: refundOption,
        custom_price: refundOption === 'custom' ? parseFloat(customPrice) : null
      });
      onSuccess();
    } catch (err) {
      toast.apiError(err, 'Не удалось удалить IDO');
    } finally {
      setLoading(false);
    }
  };

  const refundOptions = [
    { 
      id: 'none', 
      label: 'Не возвращать деньги', 
      description: 'Токены будут удалены без компенсации',
      color: COLORS.danger 
    },
    { 
      id: 'buy_price', 
      label: `По цене покупки ($${preview.refund_options.buy_price.price})`, 
      description: `Возврат: $${preview.refund_options.buy_price.total_refund.toFixed(2)}`,
      color: COLORS.success 
    },
    { 
      id: 'sell_price', 
      label: `По цене продажи ($${preview.refund_options.sell_price.price})`, 
      description: `Возврат: $${preview.refund_options.sell_price.total_refund.toFixed(2)}`,
      color: COLORS.primary 
    },
    { 
      id: 'custom', 
      label: 'По кастомной цене', 
      description: 'Укажите свою цену за токен',
      color: '#8b5cf6' 
    },
  ];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
    }} onClick={onClose}>
      <div style={{
        backgroundColor: COLORS.card,
        borderRadius: '16px',
        padding: '24px',
        width: '100%',
        maxWidth: '550px',
        maxHeight: '90vh',
        overflowY: 'auto',
      }} onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: `${COLORS.danger}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <AlertTriangle size={20} color={COLORS.danger} />
            </div>
            <div>
              <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>
                Удаление IDO
              </h3>
              <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: 0 }}>
                {project.name} (${project.symbol})
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {/* Warning */}
        {affectedUsers > 0 && (
          <div style={{
            padding: '16px',
            backgroundColor: `${COLORS.danger}15`,
            borderRadius: '10px',
            marginBottom: '20px',
            border: `1px solid ${COLORS.danger}30`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <AlertTriangle size={18} color={COLORS.danger} />
              <span style={{ color: COLORS.danger, fontWeight: '600', fontSize: '14px' }}>
                Внимание! У клиентов есть токены
              </span>
            </div>
            <div style={{ color: COLORS.text, fontSize: '13px' }}>
              <strong>{affectedUsers}</strong> клиентов владеют <strong>{totalTokens.toLocaleString()}</strong> токенами ${project.symbol}
            </div>
          </div>
        )}

        {/* Refund Options */}
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
            Выберите опцию возврата:
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {refundOptions.map(opt => (
              <div
                key={opt.id}
                onClick={() => setRefundOption(opt.id)}
                style={{
                  padding: '14px',
                  backgroundColor: refundOption === opt.id ? `${opt.color}15` : COLORS.background,
                  border: `2px solid ${refundOption === opt.id ? opt.color : COLORS.border}`,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
                      {opt.label}
                    </div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginTop: '2px' }}>
                      {opt.description}
                    </div>
                  </div>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: `2px solid ${refundOption === opt.id ? opt.color : COLORS.border}`,
                    backgroundColor: refundOption === opt.id ? opt.color : 'transparent',
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Price Input */}
        {refundOption === 'custom' && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>
              Цена за токен ($)
            </label>
            <input
              type="number"
              value={customPrice}
              onChange={(e) => setCustomPrice(e.target.value)}
              placeholder="0.00"
              step="0.000001"
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: COLORS.text,
                fontSize: '16px',
                fontWeight: '600',
                boxSizing: 'border-box',
              }}
            />
            {customPrice && parseFloat(customPrice) > 0 && (
              <div style={{ color: COLORS.success, fontSize: '13px', marginTop: '6px' }}>
                Сумма возврата: ${(totalTokens * parseFloat(customPrice)).toFixed(2)}
              </div>
            )}
          </div>
        )}

        {/* Summary */}
        <div style={{
          padding: '16px',
          backgroundColor: COLORS.background,
          borderRadius: '10px',
          marginBottom: '20px',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Затронутых клиентов</div>
              <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: '700' }}>{affectedUsers}</div>
            </div>
            <div>
              <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Токенов к удалению</div>
              <div style={{ color: COLORS.text, fontSize: '16px', fontWeight: '700' }}>{totalTokens.toLocaleString()}</div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Сумма возврата</div>
              <div style={{ color: refundOption === 'none' ? COLORS.danger : COLORS.success, fontSize: '20px', fontWeight: '700' }}>
                ${getRefundAmount().toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>
            Введите <strong style={{ color: COLORS.danger }}>{project.symbol}</strong> для подтверждения удаления:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={project.symbol}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: COLORS.background,
              border: `1px solid ${confirmText === project.symbol ? COLORS.success : COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.text,
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: COLORS.background,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.textSecondary,
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Отмена
          </button>
          <button
            onClick={handleDelete}
            disabled={loading || confirmText !== project.symbol}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: confirmText === project.symbol ? COLORS.danger : COLORS.border,
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: confirmText === project.symbol ? 'pointer' : 'not-allowed',
              opacity: confirmText === project.symbol ? 1 : 0.5,
            }}
          >
            {loading ? 'Удаление...' : 'Удалить IDO'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminIDO;
